import React from "react";
import {ChevronRight} from "lucide-react";
import {Badge, badgeVariants} from "@/components/ui/badge";
import {cn} from "@/lib/utils";
import { isPageInterceptableClick } from "@/main/navigation/NavigationPolicy";

type CardMetadataBadgeItem = {
    key?: React.Key;
    icon?: React.ReactNode;
    label?: React.ReactNode;
    value?: React.ReactNode;
    /**
     * Makes this badge a control rather than a caption.
     *
     * When set, the badge renders as a `<button>`; when absent it renders exactly the `<span>` it always
     * did, so the six card surfaces that pass no handler are untouched. Used by the profile header,
     * whose count chips are the entry points to the sections they count
     * (`designs/01-now/2026-08-11-header-count-chips-as-navigation-design.md`).
     */
    onClick?: () => void;
    /**
     * The address this badge resolves to, when it has one (`EMPTY-STATES-82`).
     *
     * Only meaningful together with `onClick`: with both, the badge renders an `<a href>` whose click
     * handler intercepts the ordinary primary click and lets every OTHER click through to the browser.
     * With `onClick` alone it stays the `<button>` it was, and with neither it stays the inert caption
     * `<span>`. So a host adopts link semantics by supplying one more field, and no existing caller
     * changes shape.
     *
     * Why it matters: five dashboard controls each resolve to a real, addressable list of the member's
     * own content, and as buttons a screen reader announced them as "37 posts, button" with nothing
     * saying they leave the view, while middle-click, Cmd-click, hover preview and "copy link address"
     * were simply unavailable — on the one surface whose job is moving between those lists.
     */
    href?: string;
    dataPlaywrightId?: string;
};

/**
 * The interaction affordances a tappable badge adds to the resting pill.
 *
 * Copied verbatim from `GroupHeaderCountChip` rather than re-derived: the group header's count chips do
 * the same job on the same kind of row, and hover/focus treatments that drift between two chip families
 * are the sort of difference nobody notices until both are on screen. One hover wash, one focus ring —
 * the design language's rules 4 and 5.
 */
/**
 * `DASHBOARD-41` + `EMPTY-STATES-63` — the chevron's reserved band, and `DASHBOARD-48` — the
 * pressable chip's boundary. Both live here, on the INTERACTIVE branch only, and neither may move
 * into `restingClasses`.
 *
 * <p><b>`[@media(pointer:coarse)]:pr-4` — the band the chevron is drawn in.</b> The pill's right
 * padding is `px-2.5` (10px). The chevron is `absolute right-1` (4px) at `size-2.5` (10px), so its
 * box spans 4-14px inward from the padding-box right edge while the text content box ends at 10px
 * in: a **4px overlap by construction**, for every chip, whatever the label. Nobody saw it while the
 * chevron was `opacity-0` at rest; `EMPTY-STATES-59` gave it `[@media(pointer:coarse)]:opacity-100`
 * — correctly, a finger has no hover — and thereby made a pre-existing collision permanently visible
 * on every phone. Seven of one run's eight dashboard states and nine of another's twelve empty
 * states reported the same fused glyph independently.
 *
 * <p>16px clears the chevron's 14px extent with 2px to spare. Gated on exactly the input device that
 * reveals the glyph, so the band exists precisely when something is drawn in it — and because a
 * coarse pointer has no hover, this padding is STATIC, not a hover response: it cannot re-open the
 * 2026-08-18 wrap oscillation the chevron's `absolute` positioning exists to prevent.
 *
 * <p><b>`border-border` — the undiluted hairline (`DASHBOARD-48`).</b> These are real `<button>`s
 * that navigate, so the pill edge is a UI-component boundary under WCAG 1.4.11's 3:1, not
 * decoration. `restingClasses`' `/60` composites `--line-color` down to 2.24:1 in light and 2.44:1
 * in dark against the page, while the card edge eight pixels away measures 3.91:1 and 4.49:1 from
 * the same frames. The fill rescues nothing (1.15:1 light, 1.07:1 dark), so the hairline is the only
 * thing bounding the control.
 *
 * <p>⚠️ <b>Both must stay in THIS constant, and it must stay LAST in the `cn` call.</b>
 * `restingClasses` is shared byte-for-byte with the inert caption branch on purpose
 * (`2026-08-11-header-count-chips-as-navigation-design.md` §3.1), so undiluting it there would
 * restyle every caption badge in the product. And `badgeClassName` is HOST-supplied — `UserHeader`
 * passes its own `border-border/60` at both of its call sites — so anything placed earlier in the
 * merge is silently overridden on the very surface these rows were filed from. `tailwind-merge`
 * keeps the later of two same-modifier `border-*` utilities and keeps BOTH `px-2.5` and a
 * differently-modified `pr-*`, which is why the coarse reserve does not disturb the shared recipe.
 * `borderTokenContrast.spec.ts` floors the composited result; `CardMetadataBadges.spec.tsx` pins the
 * divergence and the host-override case.
 */
const INTERACTIVE_BADGE_CLASSES =
    "group/nav-chip standard-hover relative touch-hit-44 cursor-pointer border-border [@media(pointer:coarse)]:pr-4 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50";

/**
 * `DASHBOARD-29` — the wrap gap a row of INTERACTIVE chips needs on a coarse pointer.
 *
 * A chip is `px-2.5 py-1 text-xs` + a 1px border, so 4 + 16 + 4 + 2 = 26 CSS px tall, and
 * `touch-hit-44` centres a transparent 44x44 `::after` on it — an overhang of (44 - 26) / 2 = 9px
 * above and below the visible box. At the hosts' `gap-1.5`/`gap-2` the wrapped pitch is 32-34px, so
 * two rows' slugs overlap by 10-12px and the later-painted sibling takes a tap aimed at its
 * neighbour. Since each chip is a DESTINATION, a mis-hit silently loads a different section.
 *
 * Non-overlap needs `chipHeight + rowGap >= 44`, i.e. a row gap of at least 18px; 20px leaves 2px of
 * margin. Applied ONLY when the row actually holds an interactive item — a caption-only row has no
 * expander and must stay byte-identical — and gated on the INPUT DEVICE rather than a viewport
 * breakpoint, which is the trap design-language rule 2 names. Nothing here touches
 * `03-tap-targets.css`: the expander itself is unchanged, only the space it is given.
 */
const INTERACTIVE_ROW_COARSE_GAP_CLASS = "[@media(pointer:coarse)]:gap-y-5";

/**
 * `DASHBOARD-21` — the navigational affordance, per wave-4 README §5.
 *
 * A count chip navigates; a caption chip does not; at rest they are deliberately the same pill
 * (`designs/01-now/2026-08-11-header-count-chips-as-navigation-design.md` §3.1 pinned the two
 * families precisely so they cannot drift). The operator's decision was to leave the RESTING recipe
 * alone and give the interactive family something visible on hover and on focus.
 *
 * A chevron is the one shape that satisfies both halves. It is not an underline — the design language
 * rules a rule drawn through a bordered pill chip out explicitly — and it is not a colour flip, which
 * rule 3 forbids. `aria-hidden` because it is an affordance, not information: the chip's accessible
 * name is unchanged.
 *
 * ⚠️ **It must never change the chip's layout size, and that is why it is `absolute`.**
 *
 * The first version of this animated `max-w-0 → max-w-4` in flow, which widened the chip by ~20px on
 * hover. In a `flex-wrap` row that is a FEEDBACK LOOP, not a transition: a chip near the container's
 * right edge grows → wraps to the next line → is no longer under the pointer → loses hover → shrinks
 * → returns to line 1 under the pointer → grows again. It never converges. Measured on devo at the
 * 2026-08-18 gate with the mouse held still, the profile header's upvotes chip oscillated between
 * `y331 x1108 w90` and `y363 x861 w110` indefinitely, and could not be clicked at all — Playwright
 * reported `element is not stable` to timeout, and a capture-phase listener recorded the press landing
 * with no button in its ancestry. Desktop only: `(pointer: coarse)` has no hover, so the loop needs a
 * fine pointer to start. Triage: `designs/08-audits-and-process/2026-08-18-devo-gate-triage-49c5d2d00.md`.
 *
 * Taking it out of flow makes the width identical hovered and at rest **by construction**, so there is
 * no arithmetic to get wrong and nothing for a host to invalidate. The alternative — widening the
 * chevron while cutting the chip's right padding by the same amount — cancels only if every host
 * passes the same `px-*`, and `badgeClassName` is host-supplied, so it would hold for the profile
 * header and silently fail for the next caller. The chevron sits in the padding the chip already has
 * (`right-1`, sized `2.5` = 10px), and only `opacity` and `transform` animate — neither reflows.
 *
 * The general rule, recorded in AGENTS.md rule 23: a hover response that changes the geometry hover
 * itself depends on is the same non-converging shape as two effects writing each other's source.
 *
 * <p><b>`EMPTY-STATES-59`, wave 8 2026-08-22 — the chevron RESTS visible on a coarse pointer.</b>
 * The treatment above answers `DASHBOARD-21` for a mouse and answers nothing at all for a finger:
 * hover does not exist on a touch device and focus-visible arrives only after the tap, so on the
 * mobile frames — where the identity card fills the entire first screen — a row of navigating count
 * chips and a row of inert caption badges were byte-identical at rest and the affordance was not
 * merely hard to find, it was ABSENT. That is the whole of what this capture added to the primary
 * row, and it is why the fix could not be another hover state.
 *
 * <p>Gated on the INPUT DEVICE, not a viewport breakpoint — design-language rule 2, and the same
 * gate `INTERACTIVE_ROW_COARSE_GAP_CLASS` above already uses, because a small window on a laptop has
 * hover and a large tablet does not. It stays `absolute`, so the resting width is still identical to
 * the hovered width by construction and the 2026-08-18 wrap loop cannot come back through this door.
 * The resting recipe of the pill itself is untouched, which is the constraint `DASHBOARD-21` records:
 * both branches deliberately share `restingClasses`, and the differentiator belongs here.
 */
const NAV_CHIP_CHEVRON_CLASS =
    "pointer-events-none absolute right-1 top-1/2 inline-flex -translate-y-1/2 translate-x-0.5 items-center opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover/nav-chip:translate-x-0 group-hover/nav-chip:opacity-100 group-focus-visible/nav-chip:translate-x-0 group-focus-visible/nav-chip:opacity-100 [@media(pointer:coarse)]:translate-x-0 [@media(pointer:coarse)]:opacity-100";

function hasContent(value: React.ReactNode): boolean {
    if (typeof value === "number") {
        return true;
    }

    if (typeof value === "string") {
        return value.trim().length > 0;
    }

    return value !== null && value !== undefined && value !== false;
}

export function CardMetadataBadges(props: {
    items: CardMetadataBadgeItem[];
    className?: string;
    badgeClassName?: string;
}) {
    const {items, className, badgeClassName} = props;
    const visibleItems = (items || []).filter(item => hasContent(item.icon) || hasContent(item.label) || hasContent(item.value));

    if (visibleItems.length === 0) {
        return null;
    }

    const hasInteractiveItem = visibleItems.some(item => Boolean(item.onClick));

    return (
        // The coarse-pointer row gap goes AFTER `className` so a host's own `gap-*` cannot merge it
        // away: `tailwind-merge` keys on the modifier, so the two survive as separate declarations and
        // the longhand `row-gap` wins over the `gap` shorthand under `(pointer: coarse)`.
        <div className={cn("flex flex-wrap gap-2", className, hasInteractiveItem && INTERACTIVE_ROW_COARSE_GAP_CLASS)}>
            {visibleItems.map((item, index) => {
                const hasIcon = hasContent(item.icon);
                const hasValue = hasContent(item.value);
                const hasLabel = hasContent(item.label);

                const contents = (
                    <>
                        {hasIcon && <span className="inline-flex items-center text-muted-foreground">{item.icon}</span>}
                        {hasValue && <span className="font-semibold text-foreground">{item.value}</span>}
                        {hasLabel && (
                            <span className={hasValue ? "text-muted-foreground" : "text-foreground"}>
                                {item.label}
                            </span>
                        )}
                    </>
                );
                const restingClasses = cn("inline-flex items-center gap-1.5 rounded-full border-border/60 bg-muted/55 px-2.5 py-1 text-xs shadow-none", badgeClassName);

                // `asChild` is not available on this Badge, so the interactive variant renders a real
                // `<button>` carrying the badge's own classes. A `<span onClick>` would look identical
                // and be unreachable by keyboard, which is the whole reason this branch exists.
                if (item.onClick) {
                    const interactiveClassName = cn(badgeVariants({ variant: "outline" }), restingClasses, INTERACTIVE_BADGE_CLASSES);
                    const affordance = (
                        <>
                            {contents}
                            <span className={NAV_CHIP_CHEVRON_CLASS} aria-hidden={true}>
                                <ChevronRight className="size-2.5"/>
                            </span>
                        </>
                    );

                    // `EMPTY-STATES-82` — an anchor whenever the host knows the destination.
                    //
                    // The handler runs on the SAME clicks it always did, and `preventDefault()` is
                    // called only for a plain primary click; a middle-click never reaches a React
                    // `onClick` at all, and a modified click falls through to the browser with the real
                    // href. So the soft navigation is unchanged and the ordinary link behaviours the row
                    // filed for — new tab, new window, hover preview, copy link address — come from the
                    // element rather than from code. Keyboard Enter activates the anchor, which fires
                    // this same handler unmodified.
                    //
                    // The classes are byte-identical to the button branch on purpose: this is the same
                    // control, and a second visual recipe for it is how the two families drift.
                    if (item.href) {
                        return (
                            <a
                                key={item.key ?? index}
                                href={item.href}
                                onClick={(event) => {
                                    if (!isPageInterceptableClick(event)) {
                                        return;
                                    }
                                    event.preventDefault();
                                    item.onClick?.();
                                }}
                                data-slot="badge"
                                data-playwright-id={item.dataPlaywrightId}
                                className={interactiveClassName}
                            >
                                {affordance}
                            </a>
                        );
                    }

                    return (
                        <button
                            key={item.key ?? index}
                            type="button"
                            onClick={item.onClick}
                            data-slot="badge"
                            data-playwright-id={item.dataPlaywrightId}
                            className={interactiveClassName}
                        >
                            {affordance}
                        </button>
                    );
                }

                return (
                    <Badge
                        key={item.key ?? index}
                        variant="outline"
                        data-playwright-id={item.dataPlaywrightId}
                        className={restingClasses}
                    >
                        {contents}
                    </Badge>
                );
            })}
        </div>
    );
}

export type {CardMetadataBadgeItem};
